import React, { useState, useEffect, useCallback } from 'react';
import { apiJson, apiPost, apiPut, apiDelete } from '../api';
import { colors, theme } from '../styles';

const ACTION_OPTIONS = ['like', 'comment', 'follow', 'unfollow'];

export default function Targets({ account }) {
  const [targets, setTargets] = useState([]);
  const [form, setForm] = useState({ target_type: 'keyword', target_value: '', action_types: 'like,comment,follow' });
  const [showBulk, setShowBulk] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [bulkActions, setBulkActions] = useState('like,comment,follow');
  const [bulkAdding, setBulkAdding] = useState(false);

  const load = useCallback(async () => {
    const data = await apiJson(`/accounts/${account.id}/targets`);
    if (data) setTargets(data.targets);
  }, [account.id]);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.target_value.trim()) return;
    await apiPost(`/accounts/${account.id}/targets`, form);
    setForm({ target_type: 'keyword', target_value: '', action_types: 'like,comment,follow' });
    load();
  };

  const bulkAdd = async () => {
    const lines = bulkText.split('\n').map(l => l.trim()).filter(Boolean);
    if (!lines.length) return;
    setBulkAdding(true);
    for (const line of lines) {
      const isAccount = line.startsWith('@');
      const isKeyword = line.startsWith('#');
      const target_type = isAccount ? 'account' : 'keyword';
      const target_value = isAccount ? line.slice(1) : line;
      await apiPost(`/accounts/${account.id}/targets`, {
        target_type,
        target_value,
        action_types: bulkActions,
      });
    }
    setBulkText('');
    setBulkAdding(false);
    setShowBulk(false);
    load();
  };

  const toggleEnabled = async (t) => {
    await apiPut(`/targets/${t.id}`, { enabled: t.enabled ? 0 : 1 });
    load();
  };

  const remove = async (id) => {
    await apiDelete(`/targets/${id}`);
    load();
  };

  const toggleAction = (actionStr, action) => {
    const current = new Set(actionStr.split(',').filter(Boolean));
    if (current.has(action)) current.delete(action);
    else current.add(action);
    return Array.from(current).join(',');
  };

  const updateActions = async (t, action) => {
    const newActions = toggleAction(t.action_types, action);
    await apiPut(`/targets/${t.id}`, { action_types: newActions });
    load();
  };

  const parsedBulkCount = bulkText.split('\n').map(l => l.trim()).filter(Boolean).length;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ color: '#fff', fontSize: 20, margin: 0 }}>Targets: @{account.username}</h2>
        <button
          style={{ ...theme.btnSecondary, fontSize: 12 }}
          onClick={() => setShowBulk(!showBulk)}
        >{showBulk ? 'Single Add' : 'Bulk Add'}</button>
      </div>

      {/* Bulk add */}
      {showBulk && (
        <div style={{ ...theme.card, marginBottom: 16, borderColor: colors.accent + '44' }}>
          <h3 style={theme.sectionTitle}>Bulk Add Targets</h3>
          <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 8, lineHeight: 1.6 }}>
            Enter one target per line. Use <code style={codeStyle}>#</code> for keywords/hashtags
            and <code style={codeStyle}>@</code> for accounts. Lines starting with @ are added as accounts,
            everything else as keywords.
          </div>
          <textarea
            style={{
              ...theme.input,
              minHeight: 120,
              fontFamily: 'monospace',
              fontSize: 13,
              resize: 'vertical',
            }}
            value={bulkText}
            onChange={e => setBulkText(e.target.value)}
            placeholder={'#athletictrainer\n#sportsmedicine\n#sportsrehab\n@somecoach\n@anotheruser'}
          />
          <label style={theme.label}>Actions for all bulk targets</label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
            {ACTION_OPTIONS.map(act => {
              const active = bulkActions.includes(act);
              return (
                <button
                  key={act}
                  style={{
                    ...theme.btnSmall,
                    background: active ? colors.accent + '33' : colors.bgInput,
                    color: active ? colors.accent : colors.textDim,
                    border: `1px solid ${active ? colors.accent : colors.borderLight}`,
                  }}
                  onClick={() => setBulkActions(toggleAction(bulkActions, act))}
                >{act}</button>
              );
            })}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              style={theme.btnSuccess}
              onClick={bulkAdd}
              disabled={bulkAdding || !parsedBulkCount}
            >
              {bulkAdding ? 'Adding...' : `Add ${parsedBulkCount} Target${parsedBulkCount !== 1 ? 's' : ''}`}
            </button>
            {parsedBulkCount > 0 && (
              <span style={{ fontSize: 12, color: colors.textDim }}>
                {parsedBulkCount} line{parsedBulkCount !== 1 ? 's' : ''} detected
              </span>
            )}
          </div>
        </div>
      )}

      {/* Single add form */}
      {!showBulk && (
        <div style={{ ...theme.card, marginBottom: 16 }}>
          <h3 style={theme.sectionTitle}>Add Target</h3>
          <div style={theme.row}>
            <div style={{ width: 140 }}>
              <label style={theme.label}>Type</label>
              <select
                style={theme.select}
                value={form.target_type}
                onChange={e => setForm({ ...form, target_type: e.target.value })}
              >
                <option value="keyword">Keyword / Hashtag</option>
                <option value="account">Account</option>
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={theme.label}>
                {form.target_type === 'keyword' ? 'Keyword or #hashtag' : 'Username'}
              </label>
              <input
                style={theme.input}
                value={form.target_value}
                onChange={e => setForm({ ...form, target_value: e.target.value })}
                placeholder={form.target_type === 'keyword' ? '#photography' : 'username'}
                onKeyDown={e => e.key === 'Enter' && add()}
              />
            </div>
          </div>

          <label style={theme.label}>Actions for this target</label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {ACTION_OPTIONS.map(act => {
              const active = form.action_types.includes(act);
              return (
                <button
                  key={act}
                  style={{
                    ...theme.btnSmall,
                    background: active ? colors.accent + '33' : colors.bgInput,
                    color: active ? colors.accent : colors.textDim,
                    border: `1px solid ${active ? colors.accent : colors.borderLight}`,
                  }}
                  onClick={() => setForm({ ...form, action_types: toggleAction(form.action_types, act) })}
                >{act}</button>
              );
            })}
          </div>
          <div style={{ marginTop: 12 }}>
            <button style={theme.btnSuccess} onClick={add}>Add Target</button>
          </div>
        </div>
      )}

      {/* Target list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {targets.map(t => (
          <div key={t.id} style={{
            ...theme.card,
            display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px',
            opacity: t.enabled ? 1 : 0.4,
          }}>
            <span style={theme.badge(t.target_type === 'keyword' ? colors.blue : colors.accent)}>
              {t.target_type}
            </span>
            <span style={{ color: '#fff', fontWeight: 500, flex: 1, fontSize: 14 }}>
              {t.target_value}
            </span>
            <div style={{ display: 'flex', gap: 4 }}>
              {ACTION_OPTIONS.map(act => {
                const active = t.action_types.includes(act);
                return (
                  <button
                    key={act}
                    style={{
                      ...theme.btnSmall,
                      fontSize: 10, padding: '3px 6px',
                      background: active ? colors.accent + '33' : 'transparent',
                      color: active ? colors.accent : colors.textDimmer,
                      border: `1px solid ${active ? colors.accent + '44' : 'transparent'}`,
                    }}
                    onClick={() => updateActions(t, act)}
                    title={`Toggle ${act}`}
                  >{act}</button>
                );
              })}
            </div>
            <button
              style={{ ...theme.btnSmall, background: t.enabled ? colors.orange : colors.green, color: '#000', fontWeight: 600 }}
              onClick={() => toggleEnabled(t)}
            >{t.enabled ? 'Disable' : 'Enable'}</button>
            <button
              style={{ ...theme.btnSmall, background: colors.redDark, color: '#fff' }}
              onClick={() => remove(t.id)}
            >X</button>
          </div>
        ))}
        {targets.length === 0 && (
          <div style={{ color: colors.textDim, fontSize: 13, textAlign: 'center', padding: 20 }}>
            No targets configured. Add keywords or accounts to target.
          </div>
        )}
      </div>
    </div>
  );
}

const codeStyle = { background: colors.bgInput, padding: '2px 6px', borderRadius: 3, fontSize: 12, color: colors.accent };
